/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { FocusZone, FocusZoneDirection } from '@fluentui/react-focus';
import { Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { List } from '@fluentui/react/lib/List';
import { IScrollablePaneStyleProps, IScrollablePaneStyles, ScrollablePane, ScrollbarVisibility } from '@fluentui/react/lib/ScrollablePane';
import { ISeparatorStyleProps, ISeparatorStyles, Separator } from '@fluentui/react/lib/Separator';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { DocumentTranslationStatus } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { TranslationDocumentSwagger, TranslatorOperationStatus } from 'src/languageStudio/core/services/translation/translatorSwagger';

type DocumentTranslatorResultStepProps = {
    translatorTracker: ProgressTrackerStore;
    documentsTranslationStatus: DocumentTranslationStatus;
    documentsTranslationResult: TranslationDocumentSwagger[];
};

const messages = defineMessages({
    nameHeaderText: { id: 'languageStudio.documentTranslatorResultStep.nameHeaderText', defaultMessage: 'Name' },
    examineResults: { id: 'languageStudio.documentTranslatorResultStep.examineResults', defaultMessage: 'Examine the results' },
    translatedFilesLabel: { id: 'languageStudio.documentTranslatorResultStep.translatedFilesLabel', defaultMessage: 'Translated files' },
    startTranslation: { id: 'languageStudio.documentTranslatorResultStep.startTranslation', defaultMessage: 'Translation in progress ...' },
    preparingDocumentForTranslation: {
        id: 'languageStudio.documentTranslatorResultStep.preparingDocumentForTranslation',
        defaultMessage: 'Preparing documents for translation ...'
    }
});

const theme = getScopedTheme('DocumentTranslatorResultStep');
const separatorStyles = classNamesFunction<ISeparatorStyleProps, ISeparatorStyles>()({ root: { padding: 0, height: '1px' } });
const listContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { position: 'relative', flex: 1 } });
const scrollPaneStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    contentContainer: { overflowX: 'hidden' }
});

const getIconName = (status: TranslatorOperationStatus) => {
    switch (status) {
        case 'Failed':
            return 'StatusErrorFull';
        case 'NotStarted':
            return 'WarningSolid';
        case 'Succeeded':
            return 'SkypeCircleCheck';
        default:
    }
};

const getIconColor = (status: TranslatorOperationStatus) => {
    switch (status) {
        case 'Failed':
            return theme.failureColor;
        case 'NotStarted':
            return theme.notStartedColor;
        case 'Succeeded':
            return theme.succeededColor;
        default:
    }
};

export const DocumentTranslatorResultStep = (props: DocumentTranslatorResultStepProps) => {
    const { documentsTranslationResult, documentsTranslationStatus, translatorTracker } = props;
    const { localize } = useLocalization();

    const getInProgressMessage = () => {
        switch (documentsTranslationStatus) {
            case 'translatingStarts':
                return localize(messages.startTranslation);
            case 'documentPreparing':
                return localize(messages.preparingDocumentForTranslation);
            default:
        }
    };

    const renderFilesListRow = (item: TranslationDocumentSwagger) => {
        const sourceUrlTokens = item.sourcePath.split('/');
        const fileName = sourceUrlTokens[sourceUrlTokens.length - 1];

        return (
            <Stack horizontal data-is-focusable verticalAlign="center" tokens={{ childrenGap: 5, padding: 10 }}>
                {item.status === 'Running' ? (
                    <Spinner size={SpinnerSize.small} />
                ) : (
                    <LuisTooltip content={item.error?.message}>
                        <Icon iconName={getIconName(item.status)} styles={{ root: { color: getIconColor(item.status) } }} />
                    </LuisTooltip>
                )}
                <LuisText>{fileName}</LuisText>
            </Stack>
        );
    };

    const renderFilesList = () => (
        <Stack styles={listContainerStyles}>
            <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={scrollPaneStyles}>
                <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                    <List items={documentsTranslationResult ?? []} onRenderCell={renderFilesListRow} />
                </FocusZone>
            </ScrollablePane>
        </Stack>
    );

    return (
        <Stack id="demoPageResultsStep" tokens={{ childrenGap: 20, maxWidth: '70%' }}>
            <LuisText fontWeight={600} variant="xLarge">
                {localize(messages.examineResults)}
            </LuisText>

            <Stack>
                <Label>{localize(messages.translatedFilesLabel)}</Label>

                <Stack grow styles={{ root: { border: `1px solid ${theme.borderColor}`, minHeight: '450px' } }}>
                    <Stack horizontal verticalAlign="center" tokens={{ padding: 10, childrenGap: 20 }}>
                        <Icon iconName="Page" />
                        <LuisText>{localize(messages.nameHeaderText)}</LuisText>
                    </Stack>
                    <Separator styles={separatorStyles} />
                    {documentsTranslationStatus !== 'TranslatingInprogress' && translatorTracker.isRunning ? (
                        <Stack grow verticalAlign="center" horizontalAlign="center">
                            <Spinner size={SpinnerSize.large} />
                            <LuisText>{getInProgressMessage()}</LuisText>
                        </Stack>
                    ) : (
                        renderFilesList()
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};
